import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid2';
import AppAnimate from '../../components/AppAnimate';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { defaultVariables } from '../../../config';
import ItemsSkeleton from '../../components/loaders/ItemsSkeleton';
import { useTranslation } from 'react-i18next';
import {CircularProgress, Divider, Pagination, Box} from '@mui/material';
import Title from '../../components/elements/Title';
import { scrollTop } from '../../../config/values';
import NothingFound from "../../components/elements/NothingFound";
import {ChevronLeft,MessageSquareOff, RefreshCw} from "lucide-react";
import Button from "@mui/material/Button";
import {Link} from "react-router-dom";
import SingleTicketListItem from "./SingleTicketListItem";
import TicketButton from "./TicketButton";

function TicketsPage({ role }) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1); // <-- added
    const [loading, setLoading] = useState(false);
    const [initialLoad, setInitialLoad] = useState(true);

    const loginToken = localStorage.getItem('ACT');
    const config = {
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${loginToken}`
        }
    };

    const loadData = async (page = 1) => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${defaultVariables.url}/tickets/get-all-lists/${role}?page=${page}`,
                config
            );
            setItems(response.data.data);
            setLastPage(response.data.last_page || 1); // <-- update total pages
        } catch (error) {
            enqueueSnackbar('Error fetching orders. Please try again.', { variant: 'error' });
            console.log(error?.response);
        } finally {
            setLoading(false);
            setInitialLoad(false);
        }
    };

    useEffect(() => {
        scrollTop();
        loadData();
    }, []);

    const handleRefresh = () => {
        setPage(1);
        setInitialLoad(true);
        loadData(1);
    };

    const handlePageChange = (event, value) => {
        setPage(value);
        loadData(value);
        scrollTop(); // optional: scroll to top when page changes
    };

    return (
        <AppAnimate animation='transition.slideUpIn' delay={200} title={role !== 'admin' ? t('support') : t('ticketLists')}>
            <Grid container spacing={2} justifyContent="center">
                <Grid item size={12} className="flex-space">
                    <Title align="left" color="text" className="panel-title">
                        {role !== 'admin' ? t('support') : t('ticketLists')}
                        <Button
                            size="small"
                            color="primary"
                            startIcon={<RefreshCw />}
                            onClick={handleRefresh}
                            disabled={loading}
                            style={{marginLeft:5}}
                        >
                            {t('refresh')}
                        </Button>
                    </Title>
                    {
                        role !== 'admin' &&  <TicketButton color="secondary" reload={handleRefresh}/>
                    }
                    {
                        role === "admin" &&
                        <Button
                            size="small"
                            color="primary"
                            variant="contained"
                            component={Link}
                            to={'/panel/manage/bulk-ticket'}
                        >
                            {t('bulkTicketTitle')}
                        </Button>
                    }
                </Grid>
                {initialLoad ? (
                    <ItemsSkeleton xs={12} md={12} lg={12} height={100}/>
                ) : (
                    <>
                        <Grid item size={12}>
                            {items.length > 0 ? (
                                <Grid container spacing={2}>
                                    {items.map((item, index) => (
                                        <Grid item size={{xs:12}} key={index}>
                                            <SingleTicketListItem item={item} reload={handleRefresh} role={role}/>
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : (
                                <NothingFound
                                    type="page"
                                    title={t('noTicketFound')}
                                    title_color="text"
                                    paragraph={t('noTicketFoundDesc')}
                                    icon={<MessageSquareOff/>}
                                >
                                    <Button
                                        variant="outlined"
                                        startIcon={<ChevronLeft/>}
                                        component={Link}
                                        to={`/`}
                                    >
                                        {t('back')}
                                    </Button>
                                </NothingFound>
                            )}
                        </Grid>

                        {items.length > 0 && lastPage > 1 && (
                            <Grid item size={12} style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
                                <Pagination
                                    count={lastPage}
                                    page={page}
                                    onChange={handlePageChange}
                                    color="primary"
                                    shape="rounded"
                                />
                            </Grid>
                        )}
                    </>
                )}
                {loading && !initialLoad && (
                    <Grid item size={12} style={{ textAlign: 'center', padding: '20px 0' }}>
                        <CircularProgress/>
                    </Grid>
                )}
            </Grid>
        </AppAnimate>
    );
}

export default TicketsPage;
